import * as yup from 'yup';
import { TestContext } from 'yup';

import { makeValidateSync } from '@cast/utils';

import { CustomLabelsModel } from 'features/cluster/autoscaler/node-selection-templates/types';

import { MAX_GPU_TIME_SLICING_REPLICAS } from '../constants';

const validateUniqueKeys = (value: string, context: TestContext): boolean => {
  const allLabels: (CustomLabelsModel | undefined)[] | undefined =
    context.from?.[1].value._customLabels;

  if (!allLabels) {
    return true;
  }

  const amountOfSameKeys = allLabels.reduce(
    (acc, label) => (label?.key === value ? acc + 1 : acc),
    0
  );

  return amountOfSameKeys <= 1;
};

export const validate = makeValidateSync(
  yup.object().shape({
    name: yup
      .string()
      .required('Name is required')
      .max(63, 'Name must be at most 63 characters')
      .matches(
        /^[a-z]([-a-z0-9]*[a-z0-9])?$/,
        'Name must start with a lowercase letter, end with a letter or digit, and contain only lowercase letters, digits, and hyphens.'
      ),
    instanceType: yup.string().required('Instance type is required'),
    availabilityZone: yup.string().required('Availability zone is required'),
    edgeConfigurationId: yup
      .string()
      .required('Edge configuration is required'),
    bootDiskGib: yup
      .number()
      .positive('Boot disk size must be a positive number')
      .typeError('Boot disk size must be a number')
      .nullable(),
    replicas: yup
      .number()
      .integer('Replicas must be an integer')
      .min(1, 'Replicas must be at least 1')
      .max(
        MAX_GPU_TIME_SLICING_REPLICAS,
        `Replicas must be at most ${MAX_GPU_TIME_SLICING_REPLICAS}`
      )
      .typeError('Replicas must be a number')
      .nullable(),
    shouldTaint: yup.boolean(),
    _customTaints: yup.array().when('shouldTaint', {
      is: true,
      then: (schema) =>
        schema.of(
          yup.object().shape({
            key: yup.string().required('Taint key is required'),
            value: yup.string().required('Taint value is required'),
          })
        ),
    }),
    shouldUseCustomLabels: yup.boolean(),
    deleteProtection: yup.boolean(),
    _customLabels: yup.array().when('shouldUseCustomLabels', {
      is: true,
      then: (schema) =>
        schema.of(
          yup.object().shape({
            key: yup
              .string()
              .required('Label key is required')
              .test('unique-key', 'This key is not unique', validateUniqueKeys),
            value: yup.string().required('Label value is required'),
          })
        ),
    }),
  })
);
